import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoConfig from './seo.config.json';

type SchemaPageType = 'WebPage' | 'CollectionPage' | 'CheckoutPage';
type SeoConfig = { title: string; description: string; url: string; image: string; pageType?: SchemaPageType; aboutId?: string };

const siteUrl = 'https://learnaihub.net';
const organizationId = `${siteUrl}/#organization`;
const websiteId = `${siteUrl}/#website`;
const educationServiceId = `${siteUrl}/#ai-education-service`;
const logoUrl = `${siteUrl}/logo.webp`;

const seoByPath = seoConfig as Record<string, SeoConfig>;

const setMetaTag = (selector: string, attribute: 'name' | 'property', value: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, value); document.head.appendChild(element); }
  element.setAttribute('content', content);
};

const setCanonical = (href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) { element = document.createElement('link'); element.setAttribute('rel', 'canonical'); document.head.appendChild(element); }
  element.setAttribute('href', href);
};

const globalOrganization = {
  '@type': 'Organization',
  '@id': organizationId,
  name: 'Learn AI Hub',
  legalName: 'Dershelas Ltd',
  url: `${siteUrl}/`,
  logo: {
    '@type': 'ImageObject',
    '@id': `${siteUrl}/#logo`,
    url: logoUrl,
    contentUrl: logoUrl,
  },
  email: 'support@learnaihub.net',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Stadiou 15',
    postalCode: '2867',
    addressLocality: 'Nicosia',
    addressCountry: 'Cyprus',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'support@learnaihub.net',
  },
};

const globalWebSite = {
  '@type': 'WebSite',
  '@id': websiteId,
  url: `${siteUrl}/`,
  name: 'Learn AI Hub',
  publisher: {
    '@id': organizationId,
  },
};

const globalService = {
  '@type': 'Service',
  '@id': educationServiceId,
  name: 'Learn AI Hub Online Education',
  serviceType: 'Artificial Intelligence Education',
  description:
    'An online educational service offering practical and beginner-friendly courses related to artificial intelligence, business automation, productivity, content creation and digital marketing.',
  url: `${siteUrl}/`,
  provider: {
    '@id': organizationId,
  },
};

const pricingOfferCatalog = {
  '@type': 'OfferCatalog',
  '@id': `${siteUrl}/pricing/#membership-plans`,
  name: 'Learn AI Hub Membership Plans',
  itemListElement: [
    {
      '@type': 'Offer',
      '@id': `${siteUrl}/pricing/#basic-plan-offer`,
      name: 'Basic Monthly Membership',
      url: `${siteUrl}/pricing/`,
      price: '29.00',
      priceCurrency: 'EUR',
      seller: {
        '@id': organizationId,
      },
      itemOffered: {
        '@type': 'Service',
        '@id': `${siteUrl}/pricing/#basic-plan`,
        name: 'Learn AI Hub Basic Plan',
        serviceType: 'Monthly AI education membership',
        description:
          'Cancel anytime. Includes access to lifestyle books only. Business books, unlimited access, and live class features are not included.',
        image: `${siteUrl}/basic.png`,
        provider: {
          '@id': organizationId,
        },
      },
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '29.00',
        priceCurrency: 'EUR',
        unitText: 'month',
        billingDuration: 'P1M',
      },
    },
    {
      '@type': 'Offer',
      '@id': `${siteUrl}/pricing/#standard-plan-offer`,
      name: 'Standard Monthly Membership',
      url: `${siteUrl}/pricing/`,
      price: '59.00',
      priceCurrency: 'EUR',
      seller: {
        '@id': organizationId,
      },
      itemOffered: {
        '@type': 'Service',
        '@id': `${siteUrl}/pricing/#standard-plan`,
        name: 'Learn AI Hub Standard Plan',
        serviceType: 'Monthly AI education membership',
        description:
          'Cancel anytime. Includes access to lifestyle books and business books. Unlimited access and live class features are not included.',
        image: `${siteUrl}/standard.png`,
        provider: {
          '@id': organizationId,
        },
      },
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '59.00',
        priceCurrency: 'EUR',
        unitText: 'month',
        billingDuration: 'P1M',
      },
    },
    {
      '@type': 'Offer',
      '@id': `${siteUrl}/pricing/#premium-plan-offer`,
      name: 'Premium Monthly Membership',
      url: `${siteUrl}/pricing/`,
      price: '97.00',
      priceCurrency: 'EUR',
      seller: {
        '@id': organizationId,
      },
      itemOffered: {
        '@type': 'Service',
        '@id': `${siteUrl}/pricing/#premium-plan`,
        name: 'Learn AI Hub Premium Plan',
        serviceType: 'Monthly AI education membership',
        description:
          'Cancel anytime. Includes unlimited access to lifestyle and business books. Also includes live classes, scheduled instructor-led live classes, direct interaction with instructors, practical live demonstrations, opportunities to ask questions during live sessions, access to upcoming and past classes through your account, and the ability to join eligible classes at their scheduled time.',
        image: `${siteUrl}/premium.png`,
        provider: {
          '@id': organizationId,
        },
      },
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '97.00',
        priceCurrency: 'EUR',
        unitText: 'month',
        billingDuration: 'P1M',
      },
    },
  ],
};

const pricingBreadcrumb = {
  '@type': 'BreadcrumbList',
  '@id': `${siteUrl}/pricing/#breadcrumb`,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${siteUrl}/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Pricing',
      item: `${siteUrl}/pricing/`,
    },
  ],
};

const buildSchema = (seo: SeoConfig, cleanPath: string) => {
  const isPricing = cleanPath === '/pricing';
  const isLegalOrAuth =
    seo.aboutId === organizationId ||
    cleanPath === '/privacy-policy' ||
    cleanPath === '/terms-and-conditions' ||
    cleanPath === '/imprint' ||
    cleanPath === '/login' ||
    cleanPath === '/signup' ||
    cleanPath === '/register' ||
    cleanPath === '/forgot-password' ||
    cleanPath === '/account' ||
    cleanPath === '/unsubscribe';

  const aboutEntity = isLegalOrAuth
    ? {
        '@type': 'Organization',
        '@id': organizationId,
        name: 'Learn AI Hub',
        url: `${siteUrl}/`,
      }
    : globalService;

  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': seo.pageType || 'WebPage',
    '@id': `${seo.url}#webpage`,
    url: seo.url,
    name: seo.title,
    description: seo.description,
    isPartOf: globalWebSite,
    publisher: globalOrganization,
    about: aboutEntity,
  };

  if (isPricing) {
    schema.mainEntity = pricingOfferCatalog;
    schema.breadcrumb = pricingBreadcrumb;
  }

  return schema;
};

const removeCanonical = () => {
  const element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (element) {
    element.remove();
  }
};

const removeSchema = () => {
  const element = document.getElementById('route-schema');
  if (element) {
    element.remove();
  }
};

const setSchema = (seo: SeoConfig, cleanPath: string) => {
  let element = document.getElementById('route-schema') as HTMLScriptElement | null;
  if (!element) { element = document.createElement('script'); element.id = 'route-schema'; element.type = 'application/ld+json'; document.head.appendChild(element); }
  element.textContent = JSON.stringify(buildSchema(seo, cleanPath));
};

const Seo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const cleanPath = pathname.replace(/\/+$/, '') || '/';
    const is404 = cleanPath === '/404' || (!seoByPath[cleanPath] && !seoByPath[pathname]);

    const notFoundSeo: SeoConfig = {
      title: '404 - Page Not Found | LearnAI Hub',
      description: 'The page you are looking for does not exist or has been moved.',
      url: `${siteUrl}${pathname}`,
      image: logoUrl,
    };

    const seo = is404 ? notFoundSeo : (seoByPath[cleanPath] || seoByPath[pathname] || seoByPath['/']);

    document.title = seo.title;
    setMetaTag('meta[name="description"]', 'name', 'description', seo.description);
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', seo.title);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', seo.description);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', seo.url);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', seo.image);
    setMetaTag('meta[property="og:image:width"]', 'property', 'og:image:width', '1200');
    setMetaTag('meta[property="og:image:height"]', 'property', 'og:image:height', '630');
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', seo.image);

    if (is404) {
      setMetaTag('meta[name="robots"]', 'name', 'robots', 'noindex, nofollow');
      removeCanonical();
      removeSchema();
    } else {
      setMetaTag('meta[name="robots"]', 'name', 'robots', 'index, follow');
      setCanonical(seo.url);
      setSchema(seo, cleanPath);
    }
  }, [pathname]);
  return null;
};

export default Seo;
